import { useQuery } from "@tanstack/react-query";
import { Navigate, useLocation, useParams } from "react-router-dom";

import { AiVisibilityEngineStatus } from "~/components/siteSpeed/AiVisibilityEngineStatus.tsx";
import {
  isDomainPrerenderingStatusHealthy,
  isDomainReady,
} from "~/components/siteSpeed/helpers.tsx";
import { domainKeys } from "~/components/siteSpeed/queryKeys";
import { useConfigurationBreadcrumbs } from "~/components/siteSpeed/useVisibilityEngineRoutes";
import Breadcrumbs from "~/components/ui/breadcrumbs/Breadcrumbs";
import { AlertCard } from "~/components/ui/card/AlertCard";
import { PAGE_TITLES } from "~/configs/pageTitles";
import { useTeam } from "~/contexts/TeamProvider.tsx";
import { parseStepFromPath } from "~/contexts/WizardContext";
import { usePageTitle } from "~/hooks/usePageTitle";
import { routes } from "~/router";

import { apiClient } from "../../../api/ApiClient";
import SiteSpeed from "../../../components/siteSpeed/SiteSpeed";
import Wizzard from "../../../components/siteSpeed/wizzard/Wizzard";
import { WizardProvider } from "../../../contexts/WizardContext";
import styles from "./SiteSpeedPage.module.scss";

const SiteSpeedDomainPage = () => {
  usePageTitle(PAGE_TITLES.aiVisibilityEngine);

  const { domainId } = useParams();
  const queryKey = domainKeys.detail(domainId!);
  const { team } = useTeam();
  const breadcrumbs = useConfigurationBreadcrumbs();
  const location = useLocation();

  const {
    data: domain,
    isPending,
    isError,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await apiClient.domains.domainsDetail(Number(domainId));

      return response?.data?.domain;
    },
  });

  if (team && !team.subscription?.price?.product?.prerendering) {
    return <Navigate to={routes.home()} replace />;
  }

  if (!domain || isError) {
    return null;
  }

  const isReady = isDomainReady(domain);

  const currentStep =
    parseStepFromPath(location.pathname, domain?.wizard_steps || []) || "";
  // ONLY FOR Wizard: do not show warning on "setup" and "verification" step
  const showSitesStatuses = !["setup", "verification"].includes(currentStep);

  return (
    <div className={styles.page}>
      {!isDomainPrerenderingStatusHealthy(domain) && showSitesStatuses && (
        <AlertCard
          className={styles.aiVisabilityWarning}
          type="neutral"
          title="Installation Incomplete"
          content="AI Visibility Engine is not enabled"
        />
      )}

      {isReady ? (
        <SiteSpeed />
      ) : (
        <>
          <h1 className={styles.title}>{PAGE_TITLES.aiVisibilityEngine}</h1>
          {breadcrumbs && <Breadcrumbs fakeBreadcrumbs={breadcrumbs} />}
          <div className={breadcrumbs ? styles.belowBreadcrumbs : undefined}>
            <WizardProvider domain={domain}>
              {showSitesStatuses && (
                <AiVisibilityEngineStatus domain={domain} />
              )}
              <Wizzard isLoading={isPending} domain={domain} />
            </WizardProvider>
          </div>
        </>
      )}
    </div>
  );
};

export default SiteSpeedDomainPage;
